
// ======================================================================
// FILE:
// src/modules/finance/reports/vendor_ledger/pages/VendorLedgerPage.js
//
// MODULE:
// Finance & Accounts Management System
//
// FEATURE:
// Vendor Ledger
//
// PURPOSE:
// Vendor-wise payable and advance transaction inquiry.
// ======================================================================

import React, {
    useMemo,
    useState,
} from "react";

import AsyncSelect from "react-select/async";
import Select from "react-select";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import financeLookupService
    from "../../../services/financeLookupService";

import vendorLedgerService
    from "../../../services/vendorLedgerService";


// ======================================================================
// HELPERS
// ======================================================================

function amountValue(value) {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
}

function formatAmount(value, currencyCode = "INR") {
    const amount = amountValue(value);

    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: currencyCode || "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return amount.toFixed(2);
    }
}

function formatDate(value) {
    if (!value) return "-";

    const date = String(value).slice(0, 10);
    const parts = date.split("-");

    if (parts.length !== 3) return date;

    return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function transactionTypeLabel(type) {
    const labels = {
        INVOICE: "Invoice",
        PAYMENT: "Payment",
        ADVANCE: "Advance",
        ADVANCE_ADJUSTMENT: "Advance Adjustment",
    };

    return labels[type] || type || "-";
}


// ======================================================================
// COMPONENT
// ======================================================================

export default function VendorLedgerPage() {

    // --------------------------------------------------------------
    // FILTERS
    // --------------------------------------------------------------

    const [selectedVendor, setSelectedVendor] =
        useState(null);

    const [fromDate, setFromDate] =
        useState("");

    const [toDate, setToDate] =
        useState("");

    const [selectedCurrency, setSelectedCurrency] =
        useState(null);

    const [transactionType, setTransactionType] =
        useState("ALL");


    // --------------------------------------------------------------
    // DATA
    // --------------------------------------------------------------

    const [ledger, setLedger] = useState(null);

    const [loading, setLoading] = useState(false);

    const [errorMessage, setErrorMessage] =
        useState("");

    const [selectedTransaction, setSelectedTransaction] =
        useState(null);


    // --------------------------------------------------------------
    // VENDOR LOOKUP
    // Existing Finance lookup service
    // --------------------------------------------------------------

    const loadVendors = async (search = "") => {
        try {
            const vendors =
                await financeLookupService.searchVendors(search);

            return (vendors || []).map((vendor) => ({
                value: vendor.id,
                label: `${vendor.bp_code || ""} - ${vendor.bp_name || ""}`
                    .replace(/^ - /, ""),
                data: vendor,
            }));
        } catch (error) {
            console.error("Vendor lookup failed:", error);
            return [];
        }
    };


    // --------------------------------------------------------------
    // CURRENCY LOOKUP
    // Existing Finance lookup service
    // --------------------------------------------------------------

    const loadCurrencies = async (search = "") => {
        try {
            return await financeLookupService.searchCurrencies(
                search
            );
        } catch (error) {
            console.error("Currency lookup failed:", error);
            return [];
        }
    };


    // --------------------------------------------------------------
    // LOAD LEDGER
    // --------------------------------------------------------------

    const handleSearch = async () => {
        if (!selectedVendor?.value) {
            setErrorMessage("Please select a vendor.");
            return;
        }

        if (fromDate && toDate && fromDate > toDate) {
            setErrorMessage(
                "From Date cannot be later than To Date."
            );
            return;
        }

        setLoading(true);
        setErrorMessage("");
        setSelectedTransaction(null);

        try {
            const result =
                await vendorLedgerService.getVendorLedger({
                    vendorId: selectedVendor.value,
                    fromDate,
                    toDate,
                });

            setLedger(result || null);
        } catch (error) {
            setLedger(null);
            setErrorMessage(
                error.response?.data?.message ||
                error.message ||
                "Unable to load Vendor Ledger."
            );
        } finally {
            setLoading(false);
        }
    };


    // --------------------------------------------------------------
    // RESET
    // --------------------------------------------------------------

    const handleReset = () => {
        setSelectedVendor(null);
        setFromDate("");
        setToDate("");
        setSelectedCurrency(null);
        setTransactionType("ALL");
        setLedger(null);
        setSelectedTransaction(null);
        setErrorMessage("");
    };


    // --------------------------------------------------------------
    // TRANSACTIONS
    // --------------------------------------------------------------

    const allTransactions = useMemo(() => {
        return Array.isArray(ledger?.transactions)
            ? ledger.transactions
            : [];
    }, [ledger]);


    const availableCurrencies = useMemo(() => {
        const codes = [
            ...new Set(
                allTransactions
                    .map((row) =>
                        row.currency_code || row.currency || ""
                    )
                    .filter(Boolean)
            ),
        ];

        return codes.map((code) => ({
            value: code,
            label: code,
        }));
    }, [allTransactions]);


    
    const filteredTransactions = useMemo(() => {
        return allTransactions.filter((row) => {
            const rowType = row.transaction_type;

            const matchesType =
                transactionType === "ALL" ||
                rowType === transactionType;

            const rowCurrency =
                row.currency_code || row.currency || "";

            const selectedCurrencyCode =
                selectedCurrency?.data?.currency_code ||
                selectedCurrency?.data?.code ||
                selectedCurrency?.currency_code ||
                selectedCurrency?.code ||
                selectedCurrency?.value;

            const matchesCurrency =
                !selectedCurrency ||
                !rowCurrency ||
                rowCurrency === selectedCurrencyCode;

            return matchesType && matchesCurrency;
        });
    }, [
        allTransactions,
        transactionType,
        selectedCurrency,
    ]);



    // --------------------------------------------------------------
    // SUMMARY
    // --------------------------------------------------------------

    const summary = useMemo(() => {
        const rows = allTransactions;

        const totalPayables = rows.reduce(
            (sum, row) =>
                sum + (
                    row.transaction_type === "INVOICE"
                        ? amountValue(row.credit)
                        : 0
                ),
            0
        );

        const totalPaid = rows.reduce(
            (sum, row) =>
                sum + (
                    row.transaction_type === "PAYMENT"
                        ? amountValue(row.debit)
                        : 0
                ),
            0
        );

        const advancePaid = rows.reduce(
            (sum, row) =>
                sum + (
                    row.transaction_type === "ADVANCE"
                        ? amountValue(
                            row.amount ??
                            row.debit ??
                            row.credit
                        )
                        : 0
                ),
            0
        );

        const advanceAdjusted = rows.reduce(
            (sum, row) =>
                sum + (
                    row.transaction_type ===
                    "ADVANCE_ADJUSTMENT"
                        ? amountValue(
                            row.debit ??
                            row.amount
                        )
                        : 0
                ),
            0
        );

        const outstandingPayable =
            amountValue(ledger?.closing_balance);

        return {
            totalPayables,
            totalPaid,
            advancePaid,
            advanceAdjusted,
            outstandingPayable,
        };
    }, [allTransactions, ledger]);


    const currencyCode =
        selectedCurrency?.value ||
        allTransactions.find(
            (row) => row.currency_code || row.currency
        )?.currency_code ||
        "INR";


    // --------------------------------------------------------------
    // SUMMARY CARDS
    // --------------------------------------------------------------

    const summaryCards = [
        {
            key: "payables",
            title: "Total Payables",
            value: formatAmount(
                summary.totalPayables,
                currencyCode
            ),
            icon: "fas fa-file-invoice-dollar",
            background: "#0d6efd",
        },
        {
            key: "paid",
            title: "Total Paid",
            value: formatAmount(
                summary.totalPaid,
                currencyCode
            ),
            icon: "fas fa-money-bill-wave",
            background: "#198754",
        },
        {
            key: "advance-paid",
            title: "Advance Paid",
            value: formatAmount(
                summary.advancePaid,
                currencyCode
            ),
            icon: "fas fa-hand-holding-usd",
            background: "#6f42c1",
        },
        {
            key: "outstanding",
            title: "Outstanding Payable",
            value: formatAmount(
                summary.outstandingPayable,
                currencyCode
            ),
            icon: "fas fa-exclamation-circle",
            background: "#dc3545",
        },
        {
            key: "advance-adjusted",
            title: "Advance Adjusted",
            value: formatAmount(
                summary.advanceAdjusted,
                currencyCode
            ),
            icon: "fas fa-exchange-alt",
            background: "#fd7e14",
        },
    ];


    // --------------------------------------------------------------
    // RENDER
    // --------------------------------------------------------------

    return (
        <>
            <FinancePageHeader
                icon="fas fa-book"
                title="Vendor Ledger"
                subtitle="Review vendor payables, settlements and advances."
                breadcrumbs={[
                    {
                        label: "Finance",
                        to: "/finance",
                    },
                    {
                        label: "Vendor Ledger",
                    },
                ]}
            />


            {/* ======================================================
                FILTER PANEL
            ====================================================== */}

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white">
                    <h6 className="fw-bold mb-0">
                        Ledger Filters
                    </h6>
                </div>

                <div className="card-body">
                    <div className="row g-3">

                        <div className="col-lg-4 col-md-6">
                            <label className="form-label">
                                Vendor <span className="text-danger">*</span>
                            </label>

                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                isClearable
                                loadOptions={loadVendors}
                                value={selectedVendor}
                                onChange={(option) => {
                                    setSelectedVendor(option);
                                    setLedger(null);
                                    setErrorMessage("");
                                }}
                                placeholder="Search vendor..."
                            />
                        </div>


                        <div className="col-lg-2 col-md-3">
                            <label className="form-label">
                                From Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={fromDate}
                                onChange={(event) =>
                                    setFromDate(event.target.value)
                                }
                            />
                        </div>


                        <div className="col-lg-2 col-md-3">
                            <label className="form-label">
                                To Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={toDate}
                                onChange={(event) =>
                                    setToDate(event.target.value)
                                }
                            />
                        </div>


                        <div className="col-lg-2 col-md-6">
                            <label className="form-label">
                                Currency
                            </label>

                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                isClearable
                                loadOptions={loadCurrencies}
                                value={selectedCurrency}
                                onChange={setSelectedCurrency}
                                placeholder="All currencies"
                            />
                        </div>


                        <div className="col-lg-2 col-md-6">
                            <label className="form-label">
                                Transaction Type
                            </label>

                            <Select
                                value={{
                                    value: transactionType,
                                    label: transactionType === "ALL"
                                        ? "All"
                                        : transactionTypeLabel(
                                            transactionType
                                        ),
                                }}
                                options={[
                                    {
                                        value: "ALL",
                                        label: "All",
                                    },
                                    {
                                        value: "INVOICE",
                                        label: "Invoice",
                                    },
                                    {
                                        value: "PAYMENT",
                                        label: "Payment",
                                    },
                                    {
                                        value: "ADVANCE",
                                        label: "Advance",
                                    },
                                    {
                                        value: "ADVANCE_ADJUSTMENT",
                                        label: "Advance Adjustment",
                                    },
                                ]}
                                onChange={(option) =>
                                    setTransactionType(
                                        option?.value || "ALL"
                                    )
                                }
                                isSearchable={false}
                            />
                        </div>

                    </div>


                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            <i className="fas fa-undo me-1" />
                            Reset
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search me-1" />
                                    View Ledger
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>


            {/* ======================================================
                ERROR
            ====================================================== */}

            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}


            {/* ======================================================
                SUMMARY CARDS
            ====================================================== */}

            {ledger && (
                <>
                    <FinanceSummaryCards
                        cards={summaryCards}
                    />


                    {/* ==================================================
                        OPENING AND CLOSING BALANCE
                    ================================================== */}

                    <div className="row g-3 mb-4">
                        <div className="col-md-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">
                                        Opening Balance
                                    </div>

                                    <div className="fs-4 fw-semibold mt-1">
                                        {formatAmount(
                                            ledger.opening_balance,
                                            currencyCode
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">
                                        Closing Balance
                                    </div>

                                    <div className="fs-4 fw-semibold mt-1">
                                        {formatAmount(
                                            ledger.closing_balance,
                                            currencyCode
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* ==================================================
                        TRANSACTION TABLE
                    ================================================== */}

                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold mb-0">
                                Ledger Transactions
                            </h6>

                            <span className="badge bg-secondary">
                                {filteredTransactions.length} records
                            </span>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Transaction Type</th>
                                        <th>Reference</th>
                                        <th className="text-end">Debit</th>
                                        <th className="text-end">Credit</th>
                                        <th className="text-end">Running Balance</th>
                                        <th className="text-center">Details</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredTransactions.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="7"
                                                className="text-center text-muted py-4"
                                            >
                                                {loading
                                                    ? "Loading transactions..."
                                                    : "No transactions found for the selected filters."}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTransactions.map(
                                            (row, index) => (
                                                <tr
                                                    key={
                                                        row.id ??
                                                        `${row.transaction_type}-${row.reference}-${index}`
                                                    }
                                                >
                                                    <td>
                                                        {formatDate(
                                                            row.date ??
                                                            row.transaction_date
                                                        )}
                                                    </td>

                                                    <td>
                                                        <span className="badge bg-light text-dark border">
                                                            {transactionTypeLabel(
                                                                row.transaction_type
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <span className="fw-semibold">
                                                            {row.reference ||
                                                             row.reference_no ||
                                                             row.voucher_no ||
                                                             row.pi_no ||
                                                             "-"}
                                                        </span>
                                                    </td>

                                                    <td className="text-end text-danger">
                                                        {amountValue(row.debit)
                                                            ? formatAmount(
                                                                row.debit,
                                                                row.currency_code || currencyCode
                                                            )
                                                            : "-"}
                                                    </td>

                                                    <td className="text-end text-success">
                                                        {amountValue(row.credit)
                                                            ? formatAmount(
                                                                row.credit,
                                                                row.currency_code || currencyCode
                                                            )
                                                            : "-"}
                                                    </td>

                                                    <td className="text-end fw-semibold">
                                                        {formatAmount(
                                                            row.running_balance,
                                                            row.currency_code || currencyCode
                                                        )}
                                                    </td>

                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="View transaction details"
                                                            onClick={() =>
                                                                setSelectedTransaction(row)
                                                            }
                                                        >
                                                            <i className="fas fa-eye" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}


            {/* ======================================================
                TRANSACTION DETAIL DRAWER
            ====================================================== */}

            {selectedTransaction && (
                <>
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100"
                        style={{
                            background: "rgba(0,0,0,0.35)",
                            zIndex: 1040,
                        }}
                        onClick={() => setSelectedTransaction(null)}
                    />

                    <div
                        className="position-fixed top-0 end-0 bg-white shadow-lg h-100"
                        style={{
                            width: "min(480px, 100vw)",
                            zIndex: 1050,
                            overflowY: "auto",
                        }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Transaction details"
                    >
                        <div className="d-flex align-items-center justify-content-between border-bottom p-3">
                            <div>
                                <h5 className="fw-bold mb-1">
                                    Transaction Details
                                </h5>

                                <div className="text-muted small">
                                    {transactionTypeLabel(
                                        selectedTransaction.transaction_type
                                    )}
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => setSelectedTransaction(null)}
                                aria-label="Close details"
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>

                        <div className="p-4">
                            <dl className="row mb-0">

                                <dt className="col-5 text-muted">
                                    Date
                                </dt>
                                <dd className="col-7">
                                    {formatDate(
                                        selectedTransaction.date ??
                                        selectedTransaction.transaction_date
                                    )}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Transaction Type
                                </dt>
                                <dd className="col-7">
                                    {transactionTypeLabel(
                                        selectedTransaction.transaction_type
                                    )}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Reference
                                </dt>
                                <dd className="col-7 text-break">
                                    {selectedTransaction.reference ||
                                     selectedTransaction.reference_no ||
                                     selectedTransaction.voucher_no ||
                                     selectedTransaction.pi_no ||
                                     "-"}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Description
                                </dt>
                                <dd className="col-7">
                                    {selectedTransaction.description ||
                                     selectedTransaction.remarks ||
                                     "-"}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Debit
                                </dt>
                                <dd className="col-7 text-danger">
                                    {formatAmount(
                                        selectedTransaction.debit,
                                        selectedTransaction.currency_code ||
                                        currencyCode
                                    )}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Credit
                                </dt>
                                <dd className="col-7 text-success">
                                    {formatAmount(
                                        selectedTransaction.credit,
                                        selectedTransaction.currency_code ||
                                        currencyCode
                                    )}
                                </dd>

                                <dt className="col-5 text-muted">
                                    Running Balance
                                </dt>
                                <dd className="col-7 fw-bold">
                                    {formatAmount(
                                        selectedTransaction.running_balance,
                                        selectedTransaction.currency_code ||
                                        currencyCode
                                    )}
                                </dd>

                            </dl>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
